import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../services/firebase/config";
import { COLLECTIONS } from "../constants/dashboard.constants";
import {
	aggregateOrderStatuses,
	aggregateAssignmentsByWarehouse,
	aggregatePayments,
} from "../utils/dashboard.utils";

// ─── Orders ───────────────────────────────────────────────────────────────────

/**
 * Fetches all order documents from Firestore and returns the aggregated
 * status counts array.
 *
 * @returns {Promise<{ status: string; count: number; color: string }[]>}
 * @throws {Error} re-throws with a descriptive message on failure
 */
export const fetchOrderStatusSummary = async () => {
	try {
		const snapshot = await getDocs(collection(db, COLLECTIONS.ORDERS));
		const orders = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return aggregateOrderStatuses(orders);
	} catch (error) {
		console.error("[Dashboard] fetchOrderStatusSummary failed:", error);
		throw new Error("Failed to load order status data. Please try again.");
	}
};

// ─── Assignments ──────────────────────────────────────────────────────────────

/**
 * Fetches all assignment documents from Firestore and returns the aggregated
 * warehouse-keyed status map.
 *
 * @returns {Promise<Record<string, { status: string; count: number; color: string }[]>>}
 * @throws {Error} re-throws with a descriptive message on failure
 */
export const fetchAssignmentSummary = async () => {
	try {
		const snapshot = await getDocs(collection(db, COLLECTIONS.ASSIGNMENTS));
		const assignments = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return aggregateAssignmentsByWarehouse(assignments);
	} catch (error) {
		console.error("[Dashboard] fetchAssignmentSummary failed:", error);
		throw new Error(
			"Failed to load assignment status data. Please try again.",
		);
	}
};

// ─── Payments ─────────────────────────────────────────────────────────────────

/**
 * Fetches all payment documents from Firestore and returns the aggregated
 * counts and monetary totals.
 *
 * @returns {Promise<{
 *   counts: Record<string, number>;
 *   totals: { totalBilledAmount: number; totalAmountReceived: number; totalPendingAmount: number }
 * }>}
 * @throws {Error} re-throws with a descriptive message on failure
 */
export const fetchPaymentSummary = async () => {
	try {
		const snapshot = await getDocs(collection(db, COLLECTIONS.PAYMENTS));
		const payments = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return aggregatePayments(payments);
	} catch (error) {
		console.error("[Dashboard] fetchPaymentSummary failed:", error);
		throw new Error("Failed to load payment data. Please try again.");
	}
};
